import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import BackButton from "@/components/back-button";
import Button from "@/components/button";
import { AppErrorBoundary } from "@/components/ErrorBoundary";
import { Combobox } from "@/components/combobox";
import Input from "@/components/input";
import { PlacesAutocomplete } from "@/components/places-autocomplete";
import Textarea from "@/components/textarea";
import { useCreateShiftMutation } from "@/queries/hooks/shiftHooks";
import { useGetBusinessProfileQuery } from "@/queries/hooks/businessHooks";
import { notify } from "@/utils";
import { useRole } from "@/utils/hooks/use-role";
import { Logger } from "@/utils/logger";
import { Loader2 } from "lucide-react";
import { useOrderedRoleOptions } from "@/utils/hooks/use-ordered-role-options";
import {
  ShiftBlocks,
  blockToApi,
  emptyShiftBlock,
} from "./-components/shift-blocks";
import type {
  ShiftBlock,
  ShiftBlockErrors,
} from "./-components/shift-blocks";

const TIME_RE = /^\d{2}:\d{2}$/;

export type FormValues = {
  location: string;
  place: any;
  role: string;
  wage: number;
  desc: string;
  blocks: Array<ShiftBlock>;
};

export type CreateShiftApiPayload = {
  name?: string;
  address: Record<string, any>;
  wage: number;
  days?: Array<string>;
  /** Exact days picked; each becomes its own shift. */
  dates?: Array<string>;
  role_name: string;
  role_description?: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  status: string;
};

const logger = Logger.scope("Shift Creation");

export const Route = createFileRoute("/app/$role/_app/shifts/new/")({
  beforeLoad: ({ params }) => {
    if (params.role !== "employer") {
      throw redirect({
        to: "/app/$role/dashboard",
        params: { role: params.role },
      });
    }
  },
  component: CreateShiftPage,
  staticData: {
    layout: {
      bg: "white",
      boxed: true,
    },
  },
});

function CreateShiftPage() {
  const { t } = useTranslation("shifts");
  const createShift = useCreateShiftMutation();
  const role = useRole();
  const navigate = useNavigate();
  const businessProfileQuery = useGetBusinessProfileQuery();
  const roles = useOrderedRoleOptions();

  const CreateShiftSchema = useMemo(
    () =>
      z.object({
        location: z
          .string()
          .min(1, { message: t("create.errors.locationRequired") }),
        place: z.any(),
        role: z.string().min(1, { message: t("create.errors.roleRequired") }),
        wage: z.coerce
          .number()
          .gt(0, { message: t("create.errors.wageInvalid") }),
        desc: z.string().default(""),
        blocks: z
          .array(
            z
              .object({
                dates: z.array(z.string()),
                startTime: z.string(),
                endTime: z.string(),
              })
              .superRefine((b, ctx) => {
                if (b.dates.length === 0) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["dates"],
                    message: t("create.errors.datesRequired"),
                  });
                }
                if (!TIME_RE.test(b.startTime)) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["startTime"],
                    message: t("create.errors.startTimeRequired"),
                  });
                }
                if (!TIME_RE.test(b.endTime)) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["endTime"],
                    message: t("create.errors.endTimeRequired"),
                  });
                }
                if (
                  TIME_RE.test(b.startTime) &&
                  TIME_RE.test(b.endTime) &&
                  b.endTime <= b.startTime
                ) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["endTime"],
                    message: t("create.errors.endTimeAfterStart"),
                  });
                }
              }),
          )
          .min(1),
      }),
    [t],
  );

  function parsePlaceToAddress(place: any): Record<string, any> {
    if (!place.location?.lat || !place.location?.lng) {
      throw new Error(t("create.errors.invalidPlace"));
    }
    const obj = {
      state: place.state,
      address1: place.formattedAddress,
      zipcode: place.zipCode,
      latitude: place.location?.lat,
      longitude: place.location?.lng,
      location: place.name,
    };
    return Object.fromEntries(
      Object.entries(obj).filter(([, v]) => v != null && v !== ""),
    );
  }

  const form = useForm({
    resolver: zodResolver(CreateShiftSchema),
    mode: "onSubmit",
    defaultValues: {
      blocks: [emptyShiftBlock()],
    },
  });

  // Default the Location field to the address captured during owner onboarding.
  // Only seed once, only if the user hasn't already typed/picked a place, and
  // only when we have lat/lng -- parsePlaceToAddress requires both on submit,
  // so prefilling a string without coordinates would just fail validation.
  const locationSeededRef = useRef(false);
  const business = businessProfileQuery.data?.business;
  useEffect(() => {
    if (locationSeededRef.current) return;
    const formatted = business?.address_formatted;
    const lat = business?.address_latitude;
    const lng = business?.address_longitude;
    if (!formatted || typeof lat !== "number" || typeof lng !== "number") return;
    if (form.getValues("location")) return;
    form.setValue("location", formatted);
    form.setValue("place", {
      formattedAddress: formatted,
      name: business.name ?? "",
      location: { lat, lng },
    });
    locationSeededRef.current = true;
  }, [business, form]);

  async function submitData(values: FormValues) {
    const address = parsePlaceToAddress(values.place || {});
    try {
      for (const block of values.blocks) {
        const apiBlock = blockToApi(block);
        const payload: CreateShiftApiPayload = {
          address,
          wage: typeof values.wage === "number" ? values.wage : 0,
          role_name: typeof values.role === "string" ? values.role : "",
          role_description:
            typeof values.desc === "string" ? values.desc : undefined,
          status: "PUBLISHED",
          ...apiBlock,
        };
        Logger.debug("data", { payload });
        await createShift.mutateAsync(payload as any);
      }
      notify.success(t("create.shiftCreated"));
      navigate({ to: "/app/$role/shifts", params: { role: String(role) } });
    } catch (err) {
      notify.error(err instanceof Error ? err.message : String(err));
    }
  }

  const onSubmit = async (values: FormValues) => {
    logger.debug("Form submitted with values:", values);
    await submitData(values);
  };

  function onError(errors: Record<string, unknown>) {
    logger.error("Form validation errors:", errors);
    console.error("Form validation failed:", errors);
    notify.error(t("create.fillRequired"));
  }

  // Build per-block error map from RHF errors for ShiftBlocks
  const blockErrors: Array<ShiftBlockErrors | undefined> = (() => {
    const rhfErrors = (form.formState.errors as any)?.blocks;
    if (!rhfErrors || !Array.isArray(rhfErrors)) return [];
    return rhfErrors.map((be: any) =>
      be
        ? {
            dates: be.dates?.message,
            startTime: be.startTime?.message,
            endTime: be.endTime?.message,
          }
        : undefined,
    );
  })();

  return (
    <section className="w-full max-w-full px-4 py-4 mx-auto sm:max-w-[655px] sm:py-12 pb-36 md:pb-28">
      <div className="w-full flex flex-row items-center justify-start mb-6 sm:mb-[43px]">
        <BackButton />
        <h1 className="heading-2 text-center w-full text-lg sm:text-2xl">
          {t("create.title")}
        </h1>
      </div>
      <form
        className="grid gap-4 w-full"
        onSubmit={form.handleSubmit(onSubmit, onError)}
      >
        <fieldset className="w-full">
          {/* Location with Google Autocomplete */}
          <Controller
            name="location"
            control={form.control}
            render={({ field, fieldState }) => (
              <AppErrorBoundary
                fallback={
                  <Input
                    label={t("create.location")}
                    placeholder={t("create.locationPlaceholder")}
                    value={field.value}
                    onChange={field.onChange}
                    state={fieldState.error ? "error" : "default"}
                    hint={fieldState.error?.message}
                  />
                }
              >
                <PlacesAutocomplete
                  label={t("create.location")}
                  value={field.value}
                  onPlaceSelect={(place) => {
                    if (place) {
                      field.onChange(place?.formattedAddress ?? "");
                      form.setValue("place", place ?? {});
                    }
                  }}
                  state={fieldState.error ? "error" : "default"}
                  hint={fieldState.error?.message}
                  onChange={field.onChange}
                />
              </AppErrorBoundary>
            )}
          />
          {/* Role + Wage */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="role"
              control={form.control}
              render={({ field, fieldState }) => (
                <Combobox
                  label={t("create.requiredRole")}
                  options={roles}
                  value={typeof field.value === "string" ? field.value : ""}
                  onChange={(v) =>
                    field.onChange(typeof v === "string" ? v : "")
                  }
                  state={fieldState.error ? "error" : "default"}
                  hint={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="wage"
              control={form.control}
              render={({ field, fieldState }) => (
                <Input
                  label={t("create.wage")}
                  type="number"
                  value={String(field.value ?? "")}
                  onChange={(e) =>
                    field.onChange(Number(e.currentTarget.value))
                  }
                  startAdornment={<span className="text-garnet/70">$</span>}
                  endAdornment={<span className="text-garnet/70">/hr</span>}
                  state={fieldState.error ? "error" : "default"}
                  hint={fieldState.error?.message}
                />
              )}
            />
          </div>
          <Controller
            name="desc"
            control={form.control}
            render={({ field, fieldState }) => (
              <Textarea
                onChange={(e) => field.onChange(e)}
                label={t("create.roleDescription")}
                placeholder={t("create.roleDescriptionPlaceholder")}
                rows={4}
                className="min-h-[96px]"
                state={fieldState.error ? "error" : "default"}
                hint={fieldState.error?.message}
              />
            )}
          />

          {/* Shift dates & times */}
          <div className="mt-4">
            <Controller
              name="blocks"
              control={form.control}
              render={({ field }) => (
                <ShiftBlocks
                  value={field.value as Array<ShiftBlock>}
                  onChange={field.onChange}
                  errors={blockErrors}
                  labels={{
                    blockTitle: (n) =>
                      t("create.shiftBlockTitle", { number: n }),
                    addAnother: t("create.addAnotherShift"),
                    remove: t("create.removeShift"),
                    selectDays: t("create.selectDays"),
                    from: t("create.from"),
                    to: t("create.to"),
                    perDayNote: (count) => t("create.perDayNote", { count }),
                  }}
                />
              )}
            />
          </div>

          {/* Submit */}
          <div className="pt-4 sm:pt-2">
             <Button
               type="submit"
               variant="primary"
               size="lg"
               className="w-full sm:w-[320px]"
               disabled={createShift.isPending}
             >
              {createShift.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {createShift.isPending
                ? t("create.posting")
                : t("create.postShift")}
            </Button>
          </div>
        </fieldset>
      </form>
    </section>
  );
}
